import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import {ITrainingAttachment, TrainingAttachmentFormValues} from '../../models/Training/TrainingAttachment'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'
import dayjs from 'dayjs'


export default class TrainingAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.trainingAttachmentRegistry.clear()
                this.loadTrainingAttachment()
            }
        )

        reaction(
            () => this.currentTrainingId,
            () => {
                this.page = 0
                this.trainingAttachmentRegistry.clear()
                !!this.currentTrainingId ? this.getTrainingAttachmentsByTrainingId() : this.loadTrainingAttachment()
            }
        )
    }

    trainingAttachmentRegistry = new Map()
    trainingAttachment: ITrainingAttachment | null = null
    editMode = false


    currentTrainingId = '';
    trainingAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentTrainingId = (trainingId: string) => {
        this.currentTrainingId = trainingId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!!predicate) {
            if (predicate !== 'all') {
                this.predicate.delete(predicate)
                if (!!value) this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
            } else {
                this.predicate.clear()
            }
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.trainingAttachmentCount / this.limit)
    }

    get trainingAttachmentsById() {
        return Array.from(this.trainingAttachmentRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get trainingAttachmentsByDate() {
        return Array.from(this.trainingAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
        )
    }

    getTrainingAttachment = (id: string) => {
        return this.trainingAttachmentRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.trainingAttachmentRegistry.clear()
        this.loadTrainingAttachment()
    }

    clearTrainingAttachment = () => {
        this.trainingAttachmentRegistry.clear()
    }

    loadTrainingAttachment = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            const trainingAttachmentsEnvelope = await Agent.TrainingAttachments.list(this.axiosParams);
            const {modelsDto, modelCount} = trainingAttachmentsEnvelope;
            runInAction(() => {
                this.clearTrainingAttachment()
                modelsDto.forEach((trainingAttachment: ITrainingAttachment) => {
                    this.trainingAttachmentRegistry.set(trainingAttachment.id, trainingAttachment)
                })
                this.trainingAttachmentCount = modelCount
            })
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getTrainingAttachmentById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let trainingAttachment = await Agent.TrainingAttachments.byId(id)
            runInAction(() => {
                this.trainingAttachment = trainingAttachment
                this.trainingAttachmentRegistry.set(trainingAttachment.id, trainingAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            return trainingAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getTrainingAttachmentsByTrainingId = async (trainingId?: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let trainingAttachmentEnvelope = await Agent.TrainingAttachments.byTrainingId(trainingId || this.currentTrainingId, this.axiosParams)
            const {modelsDto, modelCount} = trainingAttachmentEnvelope
            runInAction(() => {
                modelsDto.forEach(trainingAttachment => {
                    this.trainingAttachmentRegistry.set(trainingAttachment.id, trainingAttachment)
                })

                this.trainingAttachmentCount = modelCount
            })
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    createTrainingAttachment = async (trainingAttachment: TrainingAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const newTrainingAttachment = await Agent.TrainingAttachments.create(trainingAttachment)

            // @ts-ignore
            await (!!this.currentTrainingId ? this.getTrainingAttachmentsByTrainingId() : this.loadTrainingAttachment())
            runInAction(() => {
                this.trainingAttachmentRegistry.set(newTrainingAttachment.id, newTrainingAttachment)
                this.rootStore.modalStore.closeModal('AttachmentModal')
                this.rootStore.commonStore.setIsLoading(loadingId)
            })

            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    deleteTrainingAttachment = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            await Agent.TrainingAttachments.delete(id)
            runInAction(() => {
                this.trainingAttachmentRegistry.delete(id)
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('TrainingAttachmentStore')
            })
            toast.success('Deleted!')
        } catch (error) {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            this.rootStore.errorStore.addError(error)
        }
    }

}